import { useState, useEffect, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import AvisoMotoristaService from "../../services/resource/avisoTelemetriaService";
import { reduzirNome } from "../../utils/reduzirNome";
import { KPICard } from "../../componentes/chart/KPICard";

// ─── Constantes ────────────────────────────────────────────────────────────────
const MESES = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];
const TIPO_META = {
  ExcessoVelocidade:         { label: "Excesso de Velocidade",       cor: "#ef4444", icon: "⚡" },
  ExcessoMedioVelocidadeVia: { label: "Excesso Médio Vel. Via",      cor: "#f97316", icon: "📈" },
  _outros:                   { label: "Outros",                       cor: "#6366f1", icon: "⚠️" },
};

// ─── Paleta ────────────────────────────────────────────────────────────────────
const C = {
  bg:          "#f8fafc",
  surface:     "#ffffff",
  surfaceAlt:  "#f1f5f9",
  border:      "#e2e8f0",
  borderStr:   "#cbd5e1",
  text:        "#0f172a",
  textSub:     "#475569",
  muted:       "#94a3b8",
  red:         "#ef4444",
  redSoft:     "#fef2f2",
  redMid:      "#fecaca",
  orange:      "#f97316",
  orangeSoft:  "#fff7ed",
  orangeMid:   "#fed7aa",
  indigo:      "#6366f1",
  indigoSoft:  "#eef2ff",
  grid:        "#e2e8f0",
  accent:      "#0ea5e9",
  accentSoft:  "#f0f9ff",
};

// ─── Helpers ───────────────────────────────────────────────────────────────────
const getTipo = (tipo) => TIPO_META[tipo] || TIPO_META._outros;

function extrairMotorista(descricaoVeiculo) {
    // "PIK-9646 / PIK-9646/ 552" → pega a parte antes do primeiro "/"
    return descricaoVeiculo?.split("/")?.[0]?.trim() ?? "—";
}

function extrairVelocidades(descricao = "") {
    const permitida = descricao.match(/permitida\s+([\d.]+)\s*km\/h/i)?.[1];
    const veiculo = descricao.match(/veículo\s+([\d.]+)\s*km\/h/i)?.[1];
    return { permitida, veiculo };
}

function diasNoMes(mes, ano) {
    return new Date(ano, mes, 0).getDate();
}

function SectionTitle({ children }) {
    return (
        <div className="flex items-center gap-3 mb-5">
            <span className="text-sm font-bold tracking-wider uppercase" style={{ color: C.textSub }}>{children}</span>
            <div className="h-px flex-1" style={{ background: C.border }} />
        </div>
    );
}

function TipoBadge({ tipo }) {
    const meta = getTipo(tipo);
    return (
        <span className="px-2.5 py-0.5 rounded-full text-xs  whitespace-nowrap">
           {meta.label}
        </span>
    );
}

function LoadingOverlay() {
    return (
        <div className="flex flex-col items-center justify-center py-24 gap-2">
            <div className="w-10 h-10 rounded-full border-4 animate-spin"
                style={{ borderColor: C.border, borderTopColor: C.accent }} />
            <p className="text-sm font-medium" style={{ color: C.muted }}>Carregando avisos…</p>
        </div>
    );
}

function EmptyState({ msg = "Nenhum aviso encontrado para os filtros selecionados." }) {
    return (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
            <span className="text-5xl opacity-20">🚗</span>
            <p className="text-sm font-medium text-center max-w-xs" style={{ color: C.muted }}>{msg}</p>
        </div>
    );
}

// ── Gráfico: Infrações por Dia ────────────────────────────────────────────────
function ChartPorDia({ avisos, mes, ano }) {
    const totalDias = diasNoMes(mes, ano);
    const dias = Array.from({ length: totalDias }, (_, i) => i + 1);

    const porDia = useMemo(() => {
        const mapa = {};
        avisos.forEach((a) => {
            const d = new Date(a.dataHora).getDate();
            if (!mapa[d]) mapa[d] = { ExcessoVelocidade: 0, ExcessoMedioVelocidadeVia: 0, outros: 0 };
            if (a.tipo === "ExcessoVelocidade") mapa[d].ExcessoVelocidade++;
            else if (a.tipo === "ExcessoMedioVelocidadeVia") mapa[d].ExcessoMedioVelocidadeVia++;
            else mapa[d].outros++;
        });
        return mapa;
    }, [avisos]);

    const opts = {
        chart: { type: "bar", stacked: true, background: "transparent", toolbar: { show: false }, animations: { speed: 400 } },
        colors: [C.red, C.orange, C.indigo],
        xaxis: {
            categories: dias.map((d) => `${String(d).padStart(2, "0")}/${String(mes).padStart(2, "0")}`),
            labels: { style: { colors: C.muted, fontSize: "10px" }, rotate: -45 },
            axisBorder: { color: C.border },
        },
        yaxis: { labels: { style: { colors: C.muted } }, allowDecimals: false },
        grid: { borderColor: C.grid, strokeDashArray: 4 },
        legend: { labels: { colors: C.textSub }, position: "top" },
        tooltip: { theme: "light" },
        plotOptions: { bar: { columnWidth: "70%", borderRadius: 3 } },
        dataLabels: { enabled: false },
    };

    const series = [
        { name: "Exc. Velocidade", data: dias.map((d) => porDia[d]?.ExcessoVelocidade ?? 0) },
        { name: "Exc. Médio Vel. Via", data: dias.map((d) => porDia[d]?.ExcessoMedioVelocidadeVia ?? 0) },
        { name: "Outros", data: dias.map((d) => porDia[d]?.outros ?? 0) },
    ];

    return <ReactApexChart options={opts} series={series} type="bar" height={260} />;
}

// ── Gráfico: Distribuição por Tipo ────────────────────────────────────────────
function ChartTipos({ contagens }) {
    const entries = Object.entries(contagens).filter(([, v]) => v > 0);
    if (!entries.length) return <EmptyState />;

    const opts = {
        chart: { background: "transparent" },
        labels: entries.map(([k]) => getTipo(k).label),
        colors: entries.map(([k]) => getTipo(k).cor),
        legend: { labels: { colors: C.textSub }, position: "bottom" },
        plotOptions: {
            pie: {
                donut: {
                    size: "68%", labels: {
                        show: true,
                        total: { show: true, label: "Total", color: C.textSub, fontWeight: 700 }
                    }
                }
            }
        },
        dataLabels: { enabled: false },
        tooltip: { theme: "light" },
        stroke: { colors: [C.surface] },
    };

    return <ReactApexChart options={opts} series={entries.map(([, v]) => v)} type="donut" height={260} />;
}

// ── Gráfico: Top Motoristas ───────────────────────────────────────────────────
function ChartTopMotoristas({ avisos }) {
    const mapa = {};
    avisos.forEach((a) => {
        const m = a.funcionarioNome;
        mapa[m] = (mapa[m] || 0) + 1;
    });

    const sorted = Object.entries(mapa).sort((a, b) => b[1] - a[1]).slice(0, 10);
    if (!sorted.length) return <EmptyState />;

    const opts = {
        chart: { type: "bar", background: "transparent", toolbar: { show: false }, animations: { speed: 400 } },
        plotOptions: { bar: { horizontal: true, borderRadius: 5, dataLabels: { position: "top" } } },
        colors: [C.red],
        xaxis: { labels: { style: { colors: C.muted } }, axisBorder: { color: C.border }, allowDecimals: false },
        yaxis: { labels: { style: { colors: C.textSub, fontSize: "11px" } } },
        grid: { borderColor: C.grid, strokeDashArray: 4 },
        dataLabels: { enabled: true, style: { colors: [C.textSub], fontWeight: 700 }, offsetX: 6 },
        tooltip: { theme: "light" },
    };

    return (
        <ReactApexChart
            options={{ ...opts, xaxis: { ...opts.xaxis, categories: sorted.map(([k]) => k) } }}
            series={[{ name: "Infrações", data: sorted.map(([, v]) => v) }]}
            type="bar"
            height={Math.max(220, sorted.length * 40)}
        />
    );
}

// ── Tabela ─────────────────────────────────────────────────────────────────────
function Tabela({ avisos, search }) {
    const [sortField, setSortField] = useState("data");
    const [sortDir, setSortDir] = useState("desc");
    const [filtroInfracao, setFiltroInfracao] = useState("todos"); // todos | infracao | tolerado

    const processed = avisos.map((a) => {
        const { permitida, veiculo } = extrairVelocidades(a.descricao);

        const permitidaNum = parseFloat(permitida);
        const veiculoNum = parseFloat(veiculo);

        const limiteComTolerancia = permitidaNum ? permitidaNum * 1.05 : null;

        const isInfracao =
            limiteComTolerancia && veiculoNum
                ? veiculoNum > limiteComTolerancia
                : false;

        return {
            ...a,
            permitidaNum,
            veiculoNum,
            limiteComTolerancia,
            isInfracao,
        };
    });

    const filtered = processed
        .filter((a) => {
            const q = search.toLowerCase();

            const matchBusca =
                a.descricaoVeiculo?.toLowerCase().includes(q) ||
                a.funcionarioNome?.toLowerCase().includes(q) ||
                a.tipoDescricao?.toLowerCase().includes(q) ||
                a.descricao?.toLowerCase().includes(q) ||
                a.dataHoraFormatada?.includes(q);

            const matchFiltro =
                filtroInfracao === "todos" ||
                (filtroInfracao === "infracao" && a.isInfracao) ||
                (filtroInfracao === "tolerado" && !a.isInfracao);

            return matchBusca && matchFiltro;
        })
        .sort((a, b) => {
            let v1, v2;

            switch (sortField) {
                case "velocidade":
                    v1 = a.veiculoNum || 0;
                    v2 = b.veiculoNum || 0;
                    break;
                case "infracao":
                    v1 = a.isInfracao ? 1 : 0;
                    v2 = b.isInfracao ? 1 : 0;
                    break;
                default:
                    v1 = new Date(a.dataHoraFormatada);
                    v2 = new Date(b.dataHoraFormatada);
            }

            return sortDir === "asc" ? v1 - v2 : v2 - v1;
        });

    const toggleSort = (field) => {
        if (sortField === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDir("desc");
        }
    };

    if (!avisos.length) return <EmptyState />;

    return (
        <div className="overflow-x-auto rounded-xl" style={{ border: `1px solid ${C.border}` }}>
            <div className="flex gap-2 p-2">
                {[
                    { label: "Todos", value: "todos" },
                    { label: "Infrações", value: "infracao" },
                    { label: "Tolerados", value: "tolerado" },
                ].map((f) => (
                    <button
                        key={f.value}
                        onClick={() => setFiltroInfracao(f.value)}
                        className="px-3 py-1 rounded text-xs font-semibold"
                        style={{
                            background: filtroInfracao === f.value ? C.indigoSoft : C.surface,
                            color: filtroInfracao === f.value ? C.indigo : C.textSub,
                            border: `1px solid ${C.border}`,
                        }}
                    >
                        {f.label}
                    </button>
                ))}
            </div>
            <table className="w-full text-sm">
                <thead>
                    <tr style={{ background: C.surfaceAlt, borderBottom: `1px solid ${C.border}` }}>
                        {[
                            { label: "Data/Hora", field: "data" },
                            { label: "Veículo / Motorista", field: null },
                            { label: "Tipo", field: null },
                            { label: "Velocidade", field: "velocidade" },
                            { label: "Infração (5%)", field: "infracao" },
                            { label: "Local", field: null },
                        ].map((h) => (
                            <th
                                key={h.label}
                                onClick={() => h.field && toggleSort(h.field)}
                                className="px-4 py-3 text-left font-semibold text-xs uppercase tracking-wider cursor-pointer"
                                style={{ color: C.muted }}
                            >
                                {h.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {filtered.map((a) => {
                        const { permitida, veiculo } = extrairVelocidades(a.descricao);
                        const local = a.descricao?.match(/Endereço:\s*(.+)$/i)?.[1]?.trim() ?? "—";
                        const excesso = permitida && veiculo
                            ? (parseFloat(veiculo) - parseFloat(permitida))
                            : null;

                        const limiteComTolerancia = permitida
                            ? parseFloat(permitida) * 1.05
                            : null;

                        const isInfracao = limiteComTolerancia && veiculo
                            ? parseFloat(veiculo) > limiteComTolerancia
                            : false;

                        return (
                            <tr key={a.id} className="transition-colors duration-150 hover:bg-muted"
                                style={{ borderBottom: `1px solid ${C.border}` }}>
                                <td className="px-4 py-3 font-mono text-xs whitespace-nowrap" style={{ color: C.textSub }}>
                                    {a.dataHoraFormatada}
                                </td>
                                <td className="px-4 py-3" style={{ color: C.text }}>
                                    <span className="font-semibold">{reduzirNome(a.funcionarioNome)}</span>
                                    <br />
                                    <span className="text-xs" style={{ color: C.muted }}>{a.descricaoVeiculo}</span>
                                </td>
                                <td className="px-4 py-3">
                                    <TipoBadge tipo={a.tipo} />
                                </td>
                                <td className="px-4 py-3">
                                    {permitida && veiculo ? (
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-xs" style={{ color: C.muted }}>
                                                Limite: <strong style={{ color: C.textSub }}>{permitida} km/h</strong>
                                            </span>
                                            <span className="text-xs" style={{ color: C.muted }}>
                                                Real: <strong style={{ color: C.red }}>{veiculo} km/h</strong>
                                            </span>
                                            {excesso !== null && (
                                                <span
                                                    className="text-xs font-bold px-1.5 py-0.5 rounded w-fit"
                                                    style={{
                                                        background: isInfracao ? C.redSoft : C.orangeSoft,
                                                        color: isInfracao ? C.red : C.orange
                                                    }}
                                                >
                                                    {excesso.toFixed(1)}%
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <span style={{ color: C.muted }}>—</span>
                                    )}
                                </td>
                                <td className="px-4 py-3">
                                    {a.permitidaNum && a.veiculoNum ? (
                                        <span
                                            className="text-xs font-bold px-2 py-1 rounded"
                                            style={{
                                                background: a.isInfracao ? C.redSoft : C.orangeSoft,
                                                color: a.isInfracao ? C.red : C.orange,
                                            }}
                                        >
                                            {a.isInfracao ? "INFRAÇÃO" : "TOLERADO"}
                                        </span>
                                    ) : (
                                        <span style={{ color: C.muted }}>—</span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-xs" style={{ color: C.textSub }}>
                                    <span title={local} className="line-clamp-2">{local}</span>
                                </td>
                            </tr>
                        );
                    })}
                    {filtered.length === 0 && (
                        <tr>
                            <td colSpan={6} className="px-4 py-10 text-center" style={{ color: C.muted }}>
                                Nenhum registro encontrado para "{search}"
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            <div className="px-4 py-2 text-xs" style={{ color: C.muted, borderTop: `1px solid ${C.border}` }}>
                {filtered.length} de {avisos.length} registros
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════════
// App principal
// ═══════════════════════════════════════════════════════════════════════════════
export default function DashboardAvisoMotoristaConcept({ ano, mes }) {
    const telemetriaService = new AvisoMotoristaService();
    const [filtros, setFiltros] = useState({ tipo: "", motorista: "" });
    const [todosAvisos, setTodosAvisos] = useState([]);   // dados brutos da API
    const [loading, setLoading] = useState(false);
    const [carregou, setCarregou] = useState(false);
    const [search, setSearch] = useState("");

    // ── Carregar da API ──────────────────────────────────────────────────────────
    const carregar = () => {
        setLoading(true);

        telemetriaService
            .listarTodos({
                ano: ano,
                mes: mes
            })   // ajuste o método conforme seu service
            .then((res) => {
                setTodosAvisos(res.data ?? []);
                setCarregou(true);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => { carregar(); }, [ano, mes]);

    // ── Filtragem local (tipo + motorista) ───────────────────────────────────────
    const avisosFiltrados = useMemo(() => {
        let lista = todosAvisos;
        if (filtros.tipo) lista = lista.filter((a) => a.tipo === filtros.tipo);
        if (filtros.motorista) lista = lista.filter((a) =>
            extrairMotorista(a.descricaoVeiculo) === filtros.motorista
        );
        return lista;
    }, [todosAvisos, filtros.tipo, filtros.motorista]);

    // ── Listas para os selects ───────────────────────────────────────────────────
    const motoristas = useMemo(() => {
        const set = new Set(todosAvisos.map((a) => a.funcionarioNome));
        return [...set].sort();
    }, [todosAvisos]);

    const tipos = useMemo(() => {
        const set = new Set(todosAvisos.map((a) => a.tipo));
        return [...set].sort();
    }, [todosAvisos]);

    // ── KPIs ─────────────────────────────────────────────────────────────────────
    const contagens = useMemo(() => {
        const r = { ExcessoVelocidade: 0, ExcessoMedioVelocidadeVia: 0, _outros: 0 };
        avisosFiltrados.forEach((a) => {
            if (a.tipo === "ExcessoVelocidade") r.ExcessoVelocidade++;
            else if (a.tipo === "ExcessoMedioVelocidadeVia") r.ExcessoMedioVelocidadeVia++;
            else r._outros++;
        });
        return r;
    }, [avisosFiltrados]);

    const excessoMedio = useMemo(() => {
        const com = avisosFiltrados.filter((a) => {
            const { permitida, veiculo } = extrairVelocidades(a.descricao);
            return permitida && veiculo;
        });
        if (!com.length) return null;
        const soma = com.reduce((acc, a) => {
            const { permitida, veiculo } = extrairVelocidades(a.descricao);
            return acc + (parseFloat(veiculo) - parseFloat(permitida));
        }, 0);
        return (soma / com.length).toFixed(1);
    }, [avisosFiltrados]);

    const totalInfracoes = useMemo(() => {
        return avisosFiltrados.filter((a) => {
            const { permitida, veiculo } = extrairVelocidades(a.descricao);

            if (!permitida || !veiculo) return false;

            const limite = parseFloat(permitida) * 1.05;
            return parseFloat(veiculo) > limite;
        }).length;
    }, [avisosFiltrados]);

    const totalTolerados = useMemo(() => {
        return avisosFiltrados.filter((a) => {
            const { permitida, veiculo } = extrairVelocidades(a.descricao);

            if (!permitida || !veiculo) return false;

            const v = parseFloat(veiculo);
            const limite = parseFloat(permitida);
            const limiteTol = limite * 1.05;

            return v > limite && v <= limiteTol;
        }).length;
    }, [avisosFiltrados]);

    const periodoLabel = `${MESES[mes - 1]} / ${ano}`;

    return (
        <div>
            <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;900&display=swap" rel="stylesheet" />

            {loading ? <LoadingOverlay /> : !carregou ? (
                <EmptyState msg="Selecione o período e aplique os filtros." />
            ) : (
                <>
                    {/* ── KPIs ── */}
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mb-3">
                        <KPICard label="Total de Avisos"
                            value={avisosFiltrados.length}
                            sub={`em ${periodoLabel}`}
                            icon="🚨" color={C.red} bgColor={C.redSoft} />
                        <KPICard
                            label="Dentro da Tolerância"
                            value={totalTolerados}
                            sub="até +5%"
                            icon="⚠️"
                            color={C.orange}
                            bgColor={C.orangeSoft}
                        />
                        <KPICard
                            label="Infrações Reais"
                            value={totalInfracoes}
                            sub={`${avisosFiltrados.length ? Math.round(totalInfracoes / avisosFiltrados.length * 100) : 0}% do total`}
                            icon="🚔"
                            color={C.red}
                            bgColor={C.redSoft}
                        />
                        <KPICard label="Exc. Médio Vel."
                            value={contagens.ExcessoMedioVelocidadeVia}
                            sub={`${avisosFiltrados.length ? Math.round(contagens.ExcessoMedioVelocidadeVia / avisosFiltrados.length * 100) : 0}% do total`}
                            icon="📈" color={C.orange} bgColor={C.orangeSoft} />
                        <KPICard label="Excesso Médio"
                            value={excessoMedio != null ? `+${excessoMedio} km/h` : "—"}
                            sub="acima do limite"
                            icon="🏎️" color={C.indigo} bgColor={C.indigoSoft} />
                    </div>

                    {/* ── Gráfico por Dia + Donut ── */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
                        <div className="lg:col-span-2 rounded-2xl p-5 shadow-card"
                            style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                            <SectionTitle>Infrações por Dia</SectionTitle>
                            {avisosFiltrados.length
                                ? <ChartPorDia avisos={avisosFiltrados} mes={mes} ano={ano} />
                                : <EmptyState />}
                        </div>

                        <div className="rounded-2xl p-5 shadow-card"
                            style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                            <SectionTitle>Distribuição por Tipo</SectionTitle>
                            <ChartTipos contagens={contagens} />
                            <div className="flex flex-col gap-2 mt-4">
                                {[
                                    { k: "ExcessoVelocidade", v: contagens.ExcessoVelocidade },
                                    { k: "ExcessoMedioVelocidadeVia", v: contagens.ExcessoMedioVelocidadeVia },
                                    { k: "_outros", v: contagens._outros },
                                ].filter(({ v }) => v > 0).map(({ k, v }) => {
                                    const meta = getTipo(k);
                                    return (
                                        <div key={k} className="flex items-center gap-2 text-xs">
                                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                                style={{ background: meta.cor }} />
                                            <span className="flex-1 truncate" style={{ color: C.textSub }}>{meta.label}</span>
                                            <span className="font-bold" style={{ color: meta.cor }}>{v}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* ── Top Motoristas ── */}
                    <div className="rounded-2xl p-5 mb-3 shadow-card"
                        style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                        <SectionTitle>Top Motoristas com Mais Infrações</SectionTitle>
                        <ChartTopMotoristas avisos={avisosFiltrados} />
                    </div>

                    {/* ── Tabela ── */}
                    <div className="rounded-2xl px-4 py-5 shadow-card"
                        style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                        <SectionTitle>Detalhamento de Avisos</SectionTitle>
                        <div className="mb-4 flex items-center gap-3 flex-wrap">
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: C.muted }}>🔍</span>
                                <input type="text"
                                    placeholder="Buscar por motorista, tipo, local…"
                                    className="pl-8 pr-4 py-2 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-150"
                                    style={{ background: C.surfaceAlt, border: `1px solid ${C.borderStr}`, color: C.text, width: 300 }}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)} />
                            </div>
                        </div>
                        <Tabela avisos={avisosFiltrados} search={search} />
                    </div>
                </>
            )}
        </div>
    );
}
